/**
 * Business Development - the sales reps' workspace (2026-09-30).
 *
 *   Today     the day's visits read off the calendar, follow-ups due, a note for the day
 *   Accounts  every SNF/ALF as an account: owner, stage, next step; assign regions
 *   Map       anchors and prospects across Florida
 *   Leads     event-funnel leads, contacts, and business cards scanned in the field
 *   Sales     wins, the pipeline, and the week's touches
 *
 * Any facility anywhere opens the same account panel, so a disposition is
 * one click from wherever the rep is.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Briefcase } from 'lucide-react';

import { Meta, bdApi } from './bdApi';
import { StateLine } from './bdUi';
import TodayView from './TodayView';
import AccountsView from './AccountsView';
import BdMapView from './BdMapView';
import LeadsView from './LeadsView';
import SalesView from './SalesView';
import AccountPanel from './AccountPanel';

const TABS = [
  { key: 'today', label: 'Today' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'map', label: 'Map' },
  { key: 'leads', label: 'Leads & cards' },
  { key: 'sales', label: 'Sales' },
] as const;
type TabKey = typeof TABS[number]['key'];

export default function BusinessDevelopment() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key || 'today') as TabKey;
  const setTab = (k: TabKey) => { const p = new URLSearchParams(params); p.set('tab', k); setParams(p, { replace: true }); };

  const [meta, setMeta] = useState<Meta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<{ facilityId: string; eventId?: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadMeta = useCallback(async () => {
    setError(null);
    const r = await bdApi.meta();
    if (r.success) setMeta(r.data); else setError(r.error || 'Business Development could not be loaded.');
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const openAccount = useCallback((facilityId: string, eventId?: string) => setOpen({ facilityId, eventId }), []);
  const bump = useCallback(() => setRefreshKey((n) => n + 1), []);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center gap-3">
        <Briefcase className="w-5 h-5" aria-hidden />
        <h1 className="text-xl font-semibold">Business Development</h1>
        <p className="text-sm text-muted-foreground basis-full">Your visits, accounts, leads and wins - driven by the TM board, worked from the calendar.</p>
      </header>

      <div role="tablist" aria-label="Business Development" className="flex flex-wrap gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key} role="tab" type="button" id={`bd-tab-${t.key}`} aria-selected={tab === t.key} aria-controls={`bd-panel-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === t.key ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >{t.label}</button>
        ))}
      </div>

      <StateLine loading={!meta && !error} error={error} onRetry={loadMeta} />

      {meta && (
        <div role="tabpanel" id={`bd-panel-${tab}`} aria-labelledby={`bd-tab-${tab}`}>
          {tab === 'today' && <TodayView key={refreshKey} meta={meta} onOpen={openAccount} />}
          {tab === 'accounts' && <AccountsView meta={meta} onOpen={openAccount} refreshKey={refreshKey} />}
          {tab === 'map' && <BdMapView meta={meta} onOpen={openAccount} refreshKey={refreshKey} />}
          {tab === 'leads' && <LeadsView meta={meta} onOpen={openAccount} />}
          {tab === 'sales' && <SalesView meta={meta} onOpen={openAccount} refreshKey={refreshKey} onChanged={bump} />}
          <AccountPanel
            facilityId={open?.facilityId || null}
            calendarEventId={open?.eventId || null}
            meta={meta}
            onClose={() => setOpen(null)}
            onChanged={bump}
          />
        </div>
      )}
    </div>
  );
}
